import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Checkbox,
  Divider,
  FormControl,
  IconButton,
  ListItemText,
  MenuItem,
  Popover,
  Select,
  SelectChangeEvent,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Badge,
  ToggleButtonGroup,
  ToggleButton,
} from '@mui/material';
import {
  ChevronLeft,
  ChevronRight,
  Today,
  RestartAlt as ResetIcon,
  FilterList as FilterIcon,

} from '@mui/icons-material';
import {
  addDays,
  addMonths,
  addWeeks,
  endOfWeek,
  format,
  startOfWeek,
  subDays,
  subMonths,
  subWeeks,
} from 'date-fns';
import { uk } from 'date-fns/locale';
import { DateCalendar, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { FilterConfig } from './PageToolbar';

export type DateRangeView = 'day' | 'week' | '2weeks' | 'month';

interface CalendarToolbarProps {
  /** Prefix for localStorage persistence. */
  storageKey?: string;
  // ── Date section ──
  currentDate: Date;
  onDateChange: (date: Date) => void;
  view: DateRangeView;
  onViewChange: (view: DateRangeView) => void;
  // ── Filters section ──
  filters?: FilterConfig[];
  /** Resets only filters, not the selected date/period. */
  onResetFilters?: () => void;
  // ── Actions section ──
  extraActions?: React.ReactNode;
}

const VIEWS: { value: DateRangeView; label: string }[] = [
  { value: 'day',    label: '1d' },
  { value: 'week',   label: '1w' },
  { value: '2weeks', label: '2w' },
  { value: 'month',  label: 'M'  },
];

// ── small helpers ────────────────────────────────────────────────────────────

const SectionDivider = () => (
  <Box sx={{ width: '1px', height: 22, bgcolor: 'divider', flexShrink: 0 }} />
);

const NavBtn: React.FC<{ onClick: (e: React.MouseEvent<HTMLElement>) => void; title?: string; children: React.ReactNode; sx?: any }> = ({ onClick, title, children, sx }) => (
  <Tooltip title={title ?? ''} disableHoverListener={!title}>
    <Box
      onClick={onClick}
      role="button"
      sx={{
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        width: 26, height: 26, borderRadius: 0.5, cursor: 'pointer',
        color: 'text.secondary', flexShrink: 0,
        '&:hover': { bgcolor: 'action.hover', color: 'text.primary' },
        userSelect: 'none',
        ...sx,
      }}
    >
      {children}
    </Box>
  </Tooltip>
);

// ── main component ───────────────────────────────────────────────────────────

export const CalendarToolbar: React.FC<CalendarToolbarProps> = ({
  storageKey,
  currentDate,
  onDateChange,
  view,
  onViewChange,
  filters = [],
  onResetFilters,
  extraActions,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [pickerAnchor, setPickerAnchor] = useState<HTMLElement | null>(null);
  const [isFilterDialogOpen, setIsFilterDialogOpen] = useState(false);
  const [isDatePickerDialogOpen, setIsDatePickerDialogOpen] = useState(false);

  // Active filters count for the badge
  const activeFiltersCount = filters.reduce((acc, f) => {
    if (f.multiple && Array.isArray(f.value)) {
      const allCount = f.options?.length ?? 0;
      // Active only if it's NOT "select all" and NOT empty (if we consider empty as filtered)
      // Usually, if value.length === allCount, it's not filtering anything.
      return acc + (f.value.length !== allCount && f.value.length > 0 ? 1 : 0);
    }
    return acc + (f.value ? 1 : 0);
  }, 0);

  // Persist to localStorage whenever state changes
  const filterValuesStr = filters.map(f => `${f.id}:${JSON.stringify(f.value)}`).join('|');
  useEffect(() => {
    if (!storageKey) return;
    try {
      localStorage.setItem(`${storageKey}-toolbar`, JSON.stringify({
        currentDate: currentDate.toISOString(),
        view,
        filterValues: Object.fromEntries(filters.map(f => [f.id, f.value])),
      }));
    } catch { /* ignore */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storageKey, currentDate, view, filterValuesStr]);

  // Keyboard navigation
  const handlePrev = useCallback(() => {
    switch (view) {
      case 'month':  onDateChange(subMonths(currentDate, 1)); break;
      case 'week':   onDateChange(subWeeks(currentDate, 1));  break;
      case '2weeks': onDateChange(subWeeks(currentDate, 2));  break;
      case 'day':    onDateChange(subDays(currentDate, 1));   break;
    }
  }, [view, currentDate, onDateChange]);

  const handleNext = useCallback(() => {
    switch (view) {
      case 'month':  onDateChange(addMonths(currentDate, 1)); break;
      case 'week':   onDateChange(addWeeks(currentDate, 1));  break;
      case '2weeks': onDateChange(addWeeks(currentDate, 2));  break;
      case 'day':    onDateChange(addDays(currentDate, 1));   break;
    }
  }, [view, currentDate, onDateChange]);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
      const tag = (document.activeElement as HTMLElement)?.tagName?.toLowerCase();
      if (tag === 'input' || tag === 'textarea' || tag === 'select') return;
      if ((document.activeElement as HTMLElement)?.isContentEditable) return;
      e.key === 'ArrowLeft' ? handlePrev() : handleNext();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [handlePrev, handleNext]);

  // Date label
  const dateLabel = (() => {
    switch (view) {
      case 'month': return format(currentDate, 'MM.yyyy');
      case 'week': {
        const s = startOfWeek(currentDate, { weekStartsOn: 1 });
        const e = endOfWeek(currentDate, { weekStartsOn: 1 });
        return s.getMonth() === e.getMonth()
          ? `${format(s, 'd')}–${format(e, 'd.MM.yy')}`
          : `${format(s, 'd.MM')}–${format(e, 'd.MM.yy')}`;
      }
      case '2weeks': {
        const s = startOfWeek(currentDate, { weekStartsOn: 1 });
        const e = addDays(s, 13);
        return s.getMonth() === e.getMonth()
          ? `${format(s, 'd')}–${format(e, 'd.MM.yy')}`
          : `${format(s, 'd.MM')}–${format(e, 'd.MM.yy')}`;
      }
      case 'day': return format(currentDate, 'd.MM.yy, EEE', { locale: uk });
      default: return '';
    }
  })();

  return (
    <>
      {/* ── Single unified toolbar container ── */}
      <Box sx={{
        display: 'flex',
        alignItems: 'center',
        height: isMobile ? 48 : 40,
        bgcolor: 'background.paper',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        overflow: 'visible',
        mt: 1.5,
      }}>

        {/* ── DATE SECTION ── */}
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%', gap: 0.25, px: 0.75, flexShrink: 0 }}>
          {/* View toggle (Desktop only) */}
          {!isMobile && VIEWS.map(v => (
            <Box
              key={v.value}
              onClick={() => onViewChange(v.value)}
              role="button"
              sx={{
                px: 0.75, py: 0.25, borderRadius: 0.5,
                fontSize: '0.72rem', fontWeight: view === v.value ? 700 : 400,
                cursor: 'pointer', userSelect: 'none', lineHeight: 1.6,
                bgcolor: view === v.value ? 'primary.main' : 'transparent',
                color: view === v.value ? 'primary.contrastText' : 'text.secondary',
                '&:hover': view === v.value ? {} : { bgcolor: 'action.hover', color: 'text.primary' },
              }}
            >
              {v.label}
            </Box>
          ))}

          {!isMobile && <SectionDivider />}

          {/* Prev */}
          <NavBtn onClick={handlePrev}><ChevronLeft sx={{ fontSize: 18 }} /></NavBtn>

          {/* Date label */}
          <Typography
            variant="caption"
            onClick={() => isMobile && setIsDatePickerDialogOpen(true)}
            sx={{
              mx: 0.5, fontWeight: 600, whiteSpace: 'nowrap',
              minWidth: isMobile ? 'auto' : (view === 'day' ? 110 : 80),
              textAlign: 'center',
              cursor: isMobile ? 'pointer' : 'default',
              fontSize: isMobile ? '0.85rem' : '0.75rem',
            }}
          >
            {dateLabel}
          </Typography>

          {/* Next */}
          <NavBtn onClick={handleNext}><ChevronRight sx={{ fontSize: 18 }} /></NavBtn>

          {/* Calendar / today picker (Desktop only) */}
          {!isMobile && (
            <NavBtn
              onClick={e => setPickerAnchor(e.currentTarget)}
              title="Вибрати дату"
            >
              <Today sx={{ fontSize: 16 }} />
            </NavBtn>
          )}
        </Box>

        {/* ── FILTERS SECTION (Desktop) ── */}
        {!isMobile && filters.length > 0 && (
          <>
            <SectionDivider />
            <Box sx={{ display: 'flex', alignItems: 'center', px: 0.5, gap: 0.5, flex: 1, minWidth: 0, height: '100%' }}>
              {filters.map(filter => {
                if (filter.type === 'select') {
                  const allCount = filter.options?.length ?? 0;
                  const isMultiple = filter.multiple ?? false;
                  return (
                    <FormControl key={filter.id} size="small" sx={{ minWidth: filter.minWidth ?? 90, flexShrink: 0 }}>
                      <Select
                        value={filter.value}
                        multiple={isMultiple}
                        displayEmpty
                        onChange={(e: SelectChangeEvent<any>) => filter.onChange?.(e.target.value)}
                        renderValue={(selected: any) => {
                          const count = Array.isArray(selected) ? selected.length : (selected ? 1 : 0);
                          if (isMultiple) {
                            return count === allCount ? filter.label : `${filter.label} (${count})`;
                          }
                          return selected || filter.label;
                        }}
                        sx={{
                          fontSize: '0.75rem',
                          bgcolor: 'action.hover',
                          borderRadius: 1,
                          '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
                          '&:hover .MuiOutlinedInput-notchedOutline': { border: 'none' },
                          '&.Mui-focused .MuiOutlinedInput-notchedOutline': { border: 'none' },
                          '& .MuiSelect-select': { py: '4px', pl: '8px', fontSize: '0.75rem' },
                        }}
                      >
                        {filter.options?.map(opt => (
                          <MenuItem key={String(opt.value)} value={opt.value}>
                            {isMultiple && (
                              <Checkbox
                                size="small"
                                checked={Array.isArray(filter.value) && filter.value.includes(opt.value)}
                                sx={{ py: 0, mr: 0.5 }}
                              />
                            )}
                            <ListItemText
                              primary={opt.label}
                              primaryTypographyProps={{ fontSize: '0.8rem' }}
                            />
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  );
                }
                return null;
              })}

              {onResetFilters && (
                <Tooltip title="Скинути фільтри">
                  <IconButton size="small" onClick={onResetFilters} color="secondary" sx={{ ml: 0.5 }}>
                    <ResetIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          </>
        )}

        {/* ── ACTIONS SECTION ── */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ml: 'auto', px: 0.75, flexShrink: 0, height: '100%' }}>
          {/* Mobile Filter Button */}
          {isMobile && filters.length > 0 && (
            <IconButton size="small" onClick={() => setIsFilterDialogOpen(true)} color="inherit">
              <Badge badgeContent={activeFiltersCount} color="primary" sx={{ '& .MuiBadge-badge': { fontSize: '0.65rem', height: 16, minWidth: 16 } }}>
                <FilterIcon sx={{ fontSize: 20 }} />
              </Badge>
            </IconButton>
          )}

          {extraActions && (
            <>
              <SectionDivider />
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                {extraActions}
              </Box>
            </>
          )}
        </Box>
      </Box>

      {/* ── Separator from calendar content ── */}
      <Divider sx={{ my: 1 }} />

      {/* ── Date picker popover (Desktop) ── */}
      {!isMobile && (
        <Popover
          open={Boolean(pickerAnchor)}
          anchorEl={pickerAnchor}
          onClose={() => setPickerAnchor(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          transformOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={uk}>
            <DateCalendar
              value={currentDate}
              onChange={date => {
                if (date) { onDateChange(date); setPickerAnchor(null); }
              }}
            />
          </LocalizationProvider>
        </Popover>
      )}

      {/* ── Mobile Filter Dialog ── */}
      <Dialog
        open={isFilterDialogOpen}
        onClose={() => setIsFilterDialogOpen(false)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle sx={{ fontSize: '1rem', fontWeight: 700 }}>Фільтри</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, py: 1 }}>
            {filters.map(filter => (
              <Box key={filter.id}>
                <Typography variant="caption" sx={{ fontWeight: 700, mb: 0.5, display: 'block', color: 'text.secondary', textTransform: 'uppercase' }}>
                  {filter.label}
                </Typography>
                <FormControl fullWidth size="small">
                  <Select
                    value={filter.value}
                    multiple={filter.multiple}
                    onChange={(e) => filter.onChange?.(e.target.value)}
                    sx={{ borderRadius: 2 }}
                    renderValue={(selected: any) => {
                      if (filter.multiple && Array.isArray(selected)) {
                        if (selected.length === 0) return 'Виберіть...';
                        if (selected.length === filter.options?.length) return 'Всі';
                        return `Вибрано: ${selected.length}`;
                      }
                      return filter.options?.find(o => o.value === selected)?.label || 'Виберіть...';
                    }}
                  >
                    {filter.options?.map(opt => (
                      <MenuItem key={String(opt.value)} value={opt.value}>
                        {filter.multiple && (
                          <Checkbox
                            size="small"
                            checked={Array.isArray(filter.value) && filter.value.includes(opt.value)}
                          />
                        )}
                        <ListItemText primary={opt.label} />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            ))}
          </Box>
        </DialogContent>
        <DialogActions>
          {onResetFilters && (
            <Button onClick={() => { onResetFilters(); setIsFilterDialogOpen(false); }} color="secondary">
              Скинути все
            </Button>
          )}
          <Button onClick={() => setIsFilterDialogOpen(false)} variant="contained">
            Закрити
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Mobile Date Picker Dialog ── */}
      <Dialog
        open={isDatePickerDialogOpen}
        onClose={() => setIsDatePickerDialogOpen(false)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle sx={{ fontSize: '1rem', fontWeight: 700 }}>Вибір періоду та дати</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, alignItems: 'center' }}>
            <ToggleButtonGroup
              value={view}
              exclusive
              onChange={(_, v) => v && onViewChange(v)}
              size="small"
              fullWidth
              color="primary"
            >
              {VIEWS.map(v => (
                <ToggleButton key={v.value} value={v.value} sx={{ py: 0.5 }}>
                  {v.label}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>

            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={uk}>
              <DateCalendar
                value={currentDate}
                onChange={date => {
                  if (date) onDateChange(date);
                }}
                sx={{ width: '100%', maxHeight: 320 }}
              />
            </LocalizationProvider>
          </Box>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'space-between', px: 2 }}>
          <Button
            startIcon={<Today />}
            onClick={() => { onDateChange(new Date()); setIsDatePickerDialogOpen(false); }}
          >
            Сьогодні
          </Button>
          <Button onClick={() => setIsDatePickerDialogOpen(false)} variant="contained">
            Готово
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
